import React from "react";
import { History } from "lucide-react";
import { Tile, Badge } from "../../Primitives";
import { relTime } from "../../apikeys/shared";
import { actorName } from "../../modules/util";

const KIND = {
  setting: ["info", "Setting"], link_add: ["success", "Link added"], link_edit: ["warning", "Link edited"],
  link_retire: ["critical", "Link retired"], link_import: ["success", "Links imported"], install_upload: ["success", "Install base upload"],
  source_switch: ["warning", "Data source"],
};

function what(e) {
  if (e.kind === "setting") return `${e.label}: ${e.before} → ${e.after}`;
  if (e.kind === "link_import") return `${e.accepted} links imported, ${e.rejected} rejected · ${e.filename || ""}`;
  if (e.kind === "source_switch") return `Classification data: ${e.before} → ${e.after}${e.practice ? " (practice files — not real data)" : ""}`;
  if (e.kind === "install_upload") return `${e.accepted} rows across ${e.areas} areas, ${e.rejected} rejected · ${e.filename || ""}`;
  const l = e.link || {};
  const before = e.before ? ` (was ratio ${e.before.ratio}, ${e.before.effective_date})` : "";
  return `${l.legacy_sku} → ${l.replacement_sku} · ${l.type} · ratio ${l.ratio} · ${l.effective_date}${before}`;
}

const delta = (d) => Object.entries(d || {}).map(([c, [a, b]]) => `${c} ${a}→${b}`).join(" · ");

// Append-only change history for pipeline settings, supersession links and install base uploads.
export default function ClassificationHistory({ events }) {
  return (
    <Tile testid="rules-history" dense
          title={<span className="text-[11px] font-semibold tracking-widest uppercase text-muted inline-flex items-center gap-2"><History size={12} className="text-primary" /> Change history</span>}
          action={events && <span className="text-[10px] text-subtle font-mono-tabular">{events.length} events</span>}>
      {events?.length === 0 && <div data-testid="rules-history-empty" className="p-6 text-center text-[12px] text-muted">No changes yet — all rules are at their defaults.</div>}
      {events?.length > 0 && (
        <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
          <table className="w-full text-[11.5px]">
            <thead className="sticky top-0 bg-surface">
              <tr className="text-[10px] uppercase tracking-widest text-subtle border-b border-border">
                {["When", "Change", "Detail", "Reason", "By", "Pipeline re-run"].map(h => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {events.map(e => {
                const [status, label] = KIND[e.kind] || ["neutral", e.kind];
                return (
                  <tr key={e.id} data-testid={`rules-history-row-${e.id}`} className="border-b border-border/60 align-top hover:bg-surface-2/50">
                    <td className="px-4 py-2 font-mono-tabular text-muted whitespace-nowrap" title={e.ts}>{relTime(e.ts)}</td>
                    <td className="px-4 py-2"><Badge status={status}>{label}</Badge></td>
                    <td className="px-4 py-2 min-w-[220px] font-mono-tabular text-[11px]">{what(e)}</td>
                    <td className="px-4 py-2 text-muted min-w-[220px]">{e.reason}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{actorName(e.actor)}</td>
                    <td className="px-4 py-2 text-[10.5px] text-muted min-w-[160px]">
                      <span className="font-mono-tabular text-fg/80">{e.run_id || "—"}</span>
                      {e.cells_delta && <div>{delta(e.cells_delta) || "no cell counts changed"}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Tile>
  );
}
